import { type SelectMenuOption } from 'src/components/SelectMenu';
import { useBrands } from 'src/graphql/entities/brand';
import { useCgCustomDomains } from 'src/graphql/entities/cg-custom-domain/use-cg-custom-domain';

const UNTITLED_SEGMENT = 'Untitled segment';

export const useSegmentOptions = (partnerId: number | null) => {
  const { brands, loading: brandsLoading } = useBrands(partnerId);
  const { domains, loading: domainsLoading } = useCgCustomDomains(partnerId);

  const segments = brands.map(({ brandId, name }) => ({
    brandId,
    name: name || UNTITLED_SEGMENT,
  }));
  const segmentName = (brandId: string) =>
    segments.find((segment) => segment.brandId === brandId)?.name ?? UNTITLED_SEGMENT;

  // A segment is on one domain at most, so picking it for this one moves it here. Without an id
  // the options are for a domain not added yet.
  const segmentOptionsFor = (partnerCustomDomainId?: string): SelectMenuOption[] =>
    segments.map(({ brandId, name }) => {
      const elsewhere = domains.find(
        (other) =>
          other.partnerCustomDomainId !== partnerCustomDomainId &&
          other.brandIds.includes(brandId),
      );

      return {
        value: brandId,
        title: name,
        description: elsewhere ? `On ${elsewhere.hostname}` : undefined,
      };
    });

  const unassignedBrandIds = segments
    .filter(({ brandId }) => !domains.some((domain) => domain.brandIds.includes(brandId)))
    .map(({ brandId }) => brandId);

  // Only the first load, when no domain is known yet and every segment would look unassigned; a
  // poll or refetch keeps what is shown.
  const isLoading = (brandsLoading && !brands.length) || (domainsLoading && !domains.length);

  return { segmentOptionsFor, segmentName, unassignedBrandIds, isLoading };
};
